/**
 * SystemEventListener
 * 
 * Listens for system events from ChronoSight and triggers alerts when system metrics
 * match alert conditions. Handles events related to:
 * - CPU usage
 * - Memory usage
 * - Disk usage
 * - Network traffic (both cumulative totals and calculated rates)
 * 
 * This module processes both raw system events (systemevent-update) and
 * processed events with calculated rate metrics (systemevent-store-updated).
 * The rate-based metrics are particularly important for network throughput, which
 * is calculated as deltas between consecutive readings divided by the time interval.
 * 
 * @module SystemEventListener
 */

import BaseEventListener from './BaseEventListener';
import { AlertType, SystemEventEvents } from '../../models/AlertModels';

/**
 * Logger for SystemEventListener - only essential logging
 */
const Logger = {
  log: () => {}, // No-op to silence informational logs
  warn: (...args) => console.warn('[SystemEventListener]', ...args), // Keep warnings
  error: (...args) => console.error('[SystemEventListener]', ...args), // Keep errors
  debug: () => {} // No-op to silence debug logs
};

/**
 * Listener for system events
 * @class SystemEventListener
 * @extends BaseEventListener
 */
class SystemEventListener extends BaseEventListener {
  /**
   * Creates an instance of SystemEventListener
   * @param {Object} alertService - Service for alert operations
   */
  constructor(alertService) {
    super(alertService, 'SystemEvent');
    
    // Bind methods
    this._handleSystemEvent = this._handleSystemEvent.bind(this);
    this._handleProcessedSystemEvent = this._handleProcessedSystemEvent.bind(this);
    
    // Track server metrics for deduplication and rate limiting
    this.serverMetrics = new Map();
    this.lastProcessedTimestamps = new Map();
    
    // Rate limiting: don't process events too frequently for the same metric
    this.processingThreshold = 5000; // ms between processing the same metric
    
    // Event deduplication: track the last value for each metric to avoid processing identical values
    this.lastMetricValues = new Map();
    
    // Cache for alert results to avoid unnecessary database queries
    this.alertResultsCache = new Map();
    this.alertCacheExpiry = 60000; // 1 minute cache expiry for alerts
    this.lastAlertCacheRefresh = 0;
    
    
  }

  /**
   * Start listening for events
   * @returns {boolean} True if started successfully
   */
  start() {
    if (this.active) {
      Logger.warn('SystemEventListener already active');
      return false;
    }
    
    // Call parent start method
    const started = super.start();
    if (!started) {
      return false;
    }
    
    // Reset caches and tracking maps
    this.serverMetrics.clear();
    this.lastProcessedTimestamps.clear();
    this.lastMetricValues.clear();
    this.alertResultsCache.clear();
    this.lastAlertCacheRefresh = Date.now();
    
    // Subscribe to system events
    try {
      
      document.addEventListener('systemevent', this._handleSystemEvent);
      this.active = true;

      return true;
    } catch (error) {
      Logger.error('Error starting SystemEventListener', error);
      return false;
    }
  }

  /**
   * Stop listening for events
   * @returns {boolean} True if stopped successfully
   */
  stop() {
    if (!this.active) {
      Logger.warn('SystemEventListener not active');
      return false;
    }
    
    try {
      // Call parent stop method
      const stopped = super.stop();
      if (!stopped) {
        return false;
      }
      
      // Remove event listener for system events
      
      document.removeEventListener('systemevent', this._handleSystemEvent);
      
      // Clear caches and tracking maps to free memory
      this.serverMetrics.clear();
      this.lastProcessedTimestamps.clear();
      this.lastMetricValues.clear();
      this.alertResultsCache.clear();
      this.lastAlertCacheRefresh = 0;
      
      this.active = false;

      return true;
    } catch (error) {
      Logger.error('Error stopping SystemEventListener', error);
      return false;
    }
  }

  /**
   * Set up event listeners for system events
   * @protected
   * @returns {Promise<void>}
   */
  async _setupEventListeners() {
    // Add event listeners for system events
    // Listen for both raw system events and processed events with calculated rate metrics
    window.addEventListener('systemevent-update', this._handleSystemEvent);
    
    // Add listener for processed system events that include calculated rate metrics
    window.addEventListener('systemevent-store-updated', this._handleProcessedSystemEvent);
  }

  /**
   * Remove event listeners for system events
   * @protected
   * @returns {Promise<void>}
   */
  async _removeEventListeners() {
    // Remove event listeners for system events
    window.removeEventListener('systemevent-update', this._handleSystemEvent);
    window.removeEventListener('systemevent-store-updated', this._handleProcessedSystemEvent);
    

  }

  /**
   * Handle raw system event
   * @private
   * @param {Event} event - The event object with raw system metrics
   */
  async _handleSystemEvent(event) {
    Logger.debug('_handleSystemEvent called with event:', JSON.stringify(event.type));
    Logger.debug('Event detail structure:', event.detail ? Object.keys(event.detail) : 'No detail property');
    
    if (!event.detail || !event.detail.serverId || !event.detail.data) {
      Logger.warn('Invalid system event received', JSON.stringify(event));
      return;
    }
    
    const { serverId, data, timestamp } = event.detail;
    Logger.debug(`Processing system event for server: ${serverId}`);
    Logger.debug('Raw event data structure:', typeof data === 'object' ? Object.keys(data) : typeof data);
    
    // Safely handle timestamp conversion
    let eventTime;
    try {
      // First try to use the event_time from data if available
      if (data && data.event_time) {
        eventTime = data.event_time;
        Logger.debug(`Using event_time from data: ${eventTime}`);
      } else if (timestamp && !isNaN(new Date(timestamp).getTime())) {
        // If timestamp is valid, convert it to ISO string
        eventTime = new Date(timestamp).toISOString();
        Logger.debug(`Using timestamp from event detail: ${eventTime}`);
      } else {
        // Fallback to current time if both are invalid
        eventTime = new Date().toISOString();
        Logger.debug(`Using current time as fallback: ${eventTime}`);
      }
    } catch (error) {
      Logger.warn('Error parsing timestamp, using current time', error);
      eventTime = new Date().toISOString();
    }
    
    // Extract system data from event
    Logger.debug('Calling _extractSystemData with data:', JSON.stringify(data));
    const systemData = this._extractSystemData(data);
    if (!systemData) {
      Logger.warn('Failed to extract system data from event', JSON.stringify(data));
      return;
    }
    
    Logger.debug('Successfully extracted system data:', JSON.stringify(systemData));
    
    // Store the latest metrics for this server
    this.serverMetrics.set(serverId, {
      ...systemData,
      timestamp: Date.now(),
      eventTime
    });
    Logger.debug(`Updated server metrics cache for server: ${serverId}`);
    
    // Process the event to check for alerts
    Logger.debug(`Calling _processSystemMetrics for server: ${serverId}`);
    await this._processSystemMetrics(serverId, systemData, eventTime);
    Logger.debug('Finished processing system metrics');
  }

  /**
   * Extract system data from various event formats
   * @private
   * @param {Object} eventData - The raw event data
   * @returns {Object|null} - The extracted system metrics or null if not found
   */
  _extractSystemData(eventData) {
    if (!eventData) {
      Logger.warn('No event data to extract');
      return null;
    }
    
    Logger.debug('Extracting system data from event data:', JSON.stringify(eventData));
    Logger.debug('Event data type:', typeof eventData);
    Logger.debug('Event data keys:', Object.keys(eventData));
    
    // Handle different formats of SystemEvent data
    if (eventData.SystemEvent && typeof eventData.SystemEvent === 'object') {
      // Format 1: { SystemEvent: { cpu: {...}, memory: {...} } }
      Logger.debug('Found Format 1: SystemEvent property exists');
      Logger.debug('SystemEvent keys:', Object.keys(eventData.SystemEvent));
      return eventData.SystemEvent;
    } else if (eventData.event_data && typeof eventData.event_data === 'object') {
      // Format 2: { event_data: { cpu: {...}, memory: {...} } }
      Logger.debug('Found Format 2: event_data property exists');
      Logger.debug('event_data keys:', Object.keys(eventData.event_data));
      return eventData.event_data;
    } else if (eventData.data && typeof eventData.data === 'object') {
      // Format 3: { data: { cpu: {...}, memory: {...} } }
      Logger.debug('Found Format 3: data property exists');
      Logger.debug('data keys:', Object.keys(eventData.data));
      return eventData.data;
    } else {
      // Format 4: Direct format where the metrics are at the root level
      Logger.debug('Checking Format 4: Direct metrics at root level');
      const systemKeys = ['memory', 'cpu', 'disk', 'network'];
      const foundKeys = systemKeys.filter(key => key in eventData);
      Logger.debug('System keys found at root level:', foundKeys);
      
      const hasSystemKeys = systemKeys.some(key => 
        key in eventData && typeof eventData[key] === 'object'
      );
      
      // If there's a cpu, memory, or disk field directly, use that
      if (hasSystemKeys) {
        Logger.debug('Found system metrics in event data directly');
        const metrics = {};
        systemKeys.forEach(key => {
          if (key in eventData && typeof eventData[key] === 'object') {
            metrics[key] = eventData[key];
            Logger.debug(`Found ${key} metrics:`, JSON.stringify(metrics[key]));
          }
        });
        return eventData;
      }
      
      // Last resort: check for any numeric properties to use as metrics
      Logger.debug('Attempting to create fallback metrics from any numeric properties');
      const fallbackMetrics = {};
      let foundAny = false;
      
      Object.entries(eventData).forEach(([key, value]) => {
        if (typeof value === 'number') {
          fallbackMetrics[key] = value;
          foundAny = true;
          Logger.debug(`Found numeric property at root level: ${key} = ${value}`);
        } else if (typeof value === 'object' && value !== null) {
          // Also check first level nested properties
          Logger.debug(`Checking nested object: ${key}`);
          if (value) {
            Logger.debug(`Nested object ${key} keys:`, Object.keys(value));
            Object.entries(value).forEach(([nestedKey, nestedValue]) => {
              if (typeof nestedValue === 'number') {
                fallbackMetrics[`${key}.${nestedKey}`] = nestedValue;
                foundAny = true;
                Logger.debug(`Found numeric property in nested object: ${key}.${nestedKey} = ${nestedValue}`);
              }
            });
          }
        }
      });
      
      if (foundAny) {
        Logger.debug('Created fallback metrics from event data:', JSON.stringify(fallbackMetrics));
        return fallbackMetrics;
      }
      
      Logger.warn('Failed to extract any system metrics from event data');
      return null;
    }
  }

  /**
   * Process system metrics for a specific server
   * @private
   * @param {string} serverId - The server ID
   * @param {string} [prefix=''] - Prefix for nested keys
   * @returns {Object} Flattened metrics object
   */
  /**
   * Process system metrics for a specific server
   * @private
   * @param {string} serverId - The ID of the server
   * @param {Object} metrics - The system metrics
   * @param {string} eventTime - The timestamp of the event
   */
  async _processSystemMetrics(serverId, metrics, eventTime) {
    if (!serverId || !metrics) {
      Logger.warn('Invalid server ID or metrics for processing');
      return;
    }
    

    
    // Flatten the metrics for easier processing
    const flattenedMetrics = this._flattenMetrics(metrics, serverId);

    
    // Count metrics that will be processed
    const numericMetrics = Object.entries(flattenedMetrics)
      .filter(([_, value]) => typeof value === 'number')
      .map(([key]) => key);
    

    
    // Process each metric
    for (const [metricKey, metricValue] of Object.entries(flattenedMetrics)) {
      // Skip non-numeric values
      if (typeof metricValue !== 'number') {

        continue;
      }
      

      
      // Create a unique ID for this metric to track processing
      const metricId = `${serverId}:${metricKey}`;
      
      // Check when we last processed this metric
      const lastProcessed = this.lastProcessedTimestamps.get(metricId) || 0;
      const now = Date.now();
      
      // Skip if we've processed this metric recently (rate limiting)
      if (now - lastProcessed < this.processingThreshold) {

        continue;
      }
      
      // Check if the value has changed since last time
      const lastValue = this.lastMetricValues.get(metricId);
      if (lastValue !== undefined && lastValue === metricValue) {

        continue;
      }
      
      // Update the last value for this metric
      this.lastMetricValues.set(metricId, metricValue);
      
      // Convert disk.free and memory.available from bytes to GB
      let convertedValue = metricValue;
      if (metricKey === 'disk.free' || metricKey === 'memory.available') {
        // Convert bytes to GB (1 GB = 1024^3 bytes)
        convertedValue = Number((metricValue / (1024 * 1024 * 1024)).toFixed(2));

      }
      
      // Create event data for this specific metric
      const eventData = {
        event: metricKey,
        value: convertedValue,
        // Add the metric key as a property so rules can match on it directly
        [metricKey]: convertedValue,
        serverId,
        timestamp: now,
        eventTime
      };
      
      // Special handling for CPU percentage events
      if (metricKey === 'cpu.percent') {

        // Add additional debug information
  
      }
      
  
      
      // Process the event
      
      const result = await this.handleEvent(eventData);
      
      
      // Update last processed timestamp
      this.lastProcessedTimestamps.set(metricId, now);
      
    }
    
    
  }

  /**
   * Flatten nested metrics object into dot notation
   * @private
   * @param {Object} metrics - The nested metrics object
   * @param {string} serverId - The server ID
   * @param {string} [prefix=''] - Prefix for nested keys
   * @returns {Object} Flattened metrics object
   */
  _flattenMetrics(metrics, serverId, prefix = '') {
    const result = {};
    
    for (const [key, value] of Object.entries(metrics)) {
      const newKey = prefix ? `${prefix}.${key}` : key;
      
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        // Recursively flatten nested objects
        Object.assign(result, this._flattenMetrics(value, serverId, newKey));
      } else {
        // Add leaf node
        result[newKey] = value;
      }
    }
    
    return result;
  }

  /**
   * Handle an event by evaluating alerts and creating notifications
   * @param {Object} eventData - The event data
   * @returns {Promise<boolean>} True if the event was handled successfully
   */
  async handleEvent(eventData) {
    // Call the parent method to track statistics
    await super.handleEvent(eventData);
    
    if (!this.active) {
      
      return false;
    }
    
    try {
      
      
      // Validate that we have the required fields for alert evaluation
      if (!eventData.event) {
        Logger.warn('Event missing required field "event":', eventData);
        return false;
      }
      
      // Create a unique key for this event type
      const eventKey = `${eventData.serverId || 'global'}:${eventData.event}`;
      
      // Check if we need to refresh the alert cache
      const now = Date.now();
      if (now - this.lastAlertCacheRefresh > this.alertCacheExpiry) {
        
        this.alertResultsCache.clear();
        this.lastAlertCacheRefresh = now;
      }
      
      // Use the updated AlertService.evaluateEvent method which handles notification creation
      const { triggeredAlerts, notifications } = await this.alertService.evaluateEvent(
        eventData,
        AlertType.SYSTEM_EVENT,
        eventData.serverId
      );
      
      // Always log event processing for monitoring

      
      // Log triggered alerts
      if (triggeredAlerts.length > 0) {

        triggeredAlerts.forEach(alert => {
          
        });
      }
      
      // Log created notifications
      if (notifications.length > 0) {

        notifications.forEach(notification => {
          
        });
      }
      
      return true;
    } catch (error) {
      Logger.error('Error handling system event', error);
      return false;
    }
  }

  /**
   * Handle processed system event with rate-based metrics
   * @private
   * @param {Event} event - The custom event with processed metrics including rate calculations
   */
  async _handleProcessedSystemEvent(event) {
    try {
      Logger.debug('_handleProcessedSystemEvent called with event:', JSON.stringify(event.type));
      
      if (!event.detail || !event.detail.serverId || !event.detail.metrics) {
        Logger.warn('Invalid processed system event received', JSON.stringify(event));
        return false;
      }
      
      const { serverId, metrics, timestamp } = event.detail;
      Logger.debug(`Processing processed system event for server: ${serverId}`);
      
      // Extract the current metrics with the rate-based network metrics
      const currentMetrics = metrics.currentMetrics;
      if (!currentMetrics || !currentMetrics.network) {
        Logger.warn('No network metrics found in processed event');
        return false;
      }
      
      // Process the network rate metrics for alerting
      const networkMetrics = currentMetrics.network;
      Logger.debug(`Network metrics keys: ${Object.keys(networkMetrics)}`);
      
      // Create a timestamp for the event
      const eventTime = timestamp || new Date().toISOString();
      
      // Process the processed metrics for alerts
      await this._processSystemMetrics(serverId, currentMetrics, eventTime);
      Logger.debug('Finished processing processed system metrics with rate values');
      
      return true;
    } catch (error) {
      Logger.error('Error handling processed system event', error);
      return false;
    }
  }
}


export default SystemEventListener;
